/**
 * BRouter Routing Service.
 * Interfaces with brouter.de to provide cycling-oriented routing.
 */
import { computed, ref } from 'vue'

import { isE2eMode } from '@/e2e/e2eMode'
import { logService, LOG_CATEGORIES } from '@/services/logService'
import { isTauri } from '@/services/platformService'
import {
  DEFAULT_ROUTING_PROFILE,
  isStraightLineRoutingProfile,
  resolveBrouterProfileId,
} from '@/services/routingProfiles'
import { calculateHaversineDistance } from '@/services/trackMetricsCalculationService'

export { DEFAULT_ROUTING_PROFILE, ROUTING_PROFILES } from '@/services/routingProfiles'
export type { RoutingProfile } from '@/services/routingProfiles'

const BROUTER_BASE = 'https://brouter.route.camp/'
const E2E_ROUTE_STEPS_PER_SEGMENT = 8
const E2E_ROUTE_SPEED_METERS_PER_SECOND = 4

const pendingRoutingRequests = ref(0)
const pendingAbortControllers = new Set<AbortController>()

export const isRoutingPending = computed(() => pendingRoutingRequests.value > 0)

export type SpinningRoutePointLocation = {
  lat: number
  lon: number
}

const dedupeSpinningRoutePoints = (
  routePoints: Array<SpinningRoutePointLocation | null | undefined>,
): SpinningRoutePointLocation[] => {
  const seen = new Set<string>()
  const unique: SpinningRoutePointLocation[] = []

  routePoints.forEach((routePoint) => {
    if (!routePoint) return
    const key = `${routePoint.lat}:${routePoint.lon}`
    if (seen.has(key)) return
    seen.add(key)
    unique.push({ lat: routePoint.lat, lon: routePoint.lon })
  })

  return unique
}

/**
 * Aborts any in-flight BRouter requests. Each aborted `getRoute()` call
 * resolves to `null`, just like a network failure, so callers that already
 * handle the null case need no changes. Also clears the spinning marker.
 */
export function abortPendingRouting(): void {
  for (const controller of pendingAbortControllers) {
    controller.abort()
  }
  pendingAbortControllers.clear()
  clearSpinningRoutePoints()
}

/**
 * Currently "in progress" route point location shown as a spinning marker on the map.
 * Set by callers that start route-point work (adding, moving, deleting) so that
 * RoutePointLayer can render a visual spinner at the relevant position.
 */
export const spinningRoutePoints = ref<SpinningRoutePointLocation[]>([])

export const spinningRoutePoint = computed<SpinningRoutePointLocation | null>({
  get: () => spinningRoutePoints.value[0] ?? null,
  set: (routePoint) => {
    spinningRoutePoints.value = routePoint ? dedupeSpinningRoutePoints([routePoint]) : []
  },
})

export function setSpinningRoutePoints(
  routePoints: Array<SpinningRoutePointLocation | null | undefined>,
): void {
  spinningRoutePoints.value = dedupeSpinningRoutePoints(routePoints)
}

export function clearSpinningRoutePoints(): void {
  spinningRoutePoints.value = []
}

async function withPendingTracking<T>(fn: () => Promise<T>): Promise<T> {
  pendingRoutingRequests.value += 1
  try {
    return await fn()
  } finally {
    pendingRoutingRequests.value -= 1
  }
}

export interface RouteWaypoint {
  lat: number
  lon: number
  ele?: number
  time_offset?: number
  distance?: number
  cost_per_km?: number
  elev_cost?: number
  turn_cost?: number
  node_cost?: number
  initial_cost?: number
  way_tags?: string
  node_tags?: string
}

export interface VoiceHintData {
  nodeIndex: number
  command: number
  exitNumber?: number
  distance: number
  angle: number
}

export interface RouteResult {
  points: RouteWaypoint[]
  // Convenience lat/lon pairs (mirrors points, for existing callers)
  coordinates: [number, number][]
  distance: number
  duration: number
  // BRouter per-route stats
  creator?: string
  profile?: string
  trackLength?: number
  totalTime?: number
  filteredAscend?: number
  plainAscend?: number
  filteredDescend?: number
  plainDescend?: number
  totalEnergy?: number
  cost?: number
  voiceHints?: VoiceHintData[]
}

interface BRouterFeature {
  type: 'Feature'
  geometry: {
    type: 'LineString'
    coordinates: number[][]
  }
  properties: Record<string, unknown>
}

interface BRouterResponse {
  type: 'FeatureCollection'
  features: BRouterFeature[]
}

interface NativeBrouterRouteOutput {
  status: number
  body: string
}

function toNumber(v: unknown): number | undefined {
  if (v === undefined || v === null || v === '') return undefined
  const n = typeof v === 'number' ? v : parseFloat(String(v))
  return Number.isFinite(n) ? n : undefined
}

function toInt(v: unknown): number | undefined {
  const n = toNumber(v)
  return n === undefined ? undefined : Math.round(n)
}

interface ParsedMessageRow {
  lonMicro: number
  latMicro: number
  ele?: number
  distance?: number
  costPerKm?: number
  elevCost?: number
  turnCost?: number
  nodeCost?: number
  initialCost?: number
  wayTags?: string
  nodeTags?: string
}

/**
 * Parse BRouter's `messages` array into rows keyed by the **end-node** of each
 * described segment. Row 0 is the header (column names). Each subsequent row
 * describes a route segment that ENDS at the node whose integer microdegree
 * lon/lat is given in its `Longitude`/`Latitude` columns (e.g. "10658541").
 *
 * BRouter emits one row per segment boundary (way-tag change, junction), NOT
 * one row per geometry coordinate — so messages.length − 1 ≪ coords.length is
 * the normal case.
 */
function parseMessages(messages: unknown): ParsedMessageRow[] {
  if (!Array.isArray(messages) || messages.length < 2) return []
  const header = (messages[0] as unknown[]).map((h) => String(h))
  const col = (name: string): number => header.indexOf(name)
  const iLon = col('Longitude')
  const iLat = col('Latitude')
  const iEle = col('Elevation')
  const iDist = col('Distance')
  const iCostPerKm = col('CostPerKm')
  const iElev = col('ElevCost')
  const iTurn = col('TurnCost')
  const iNode = col('NodeCost')
  const iInit = col('InitialCost')
  const iWay = col('WayTags')
  const iNodeT = col('NodeTags')

  const rows: ParsedMessageRow[] = []
  for (let i = 1; i < messages.length; i++) {
    const row = messages[i] as unknown[]
    if (!Array.isArray(row)) continue
    const lonMicro = toInt(row[iLon])
    const latMicro = toInt(row[iLat])
    if (lonMicro === undefined || latMicro === undefined) continue
    rows.push({
      lonMicro,
      latMicro,
      ele: iEle >= 0 ? toNumber(row[iEle]) : undefined,
      distance: iDist >= 0 ? toInt(row[iDist]) : undefined,
      costPerKm: iCostPerKm >= 0 ? toInt(row[iCostPerKm]) : undefined,
      elevCost: iElev >= 0 ? toInt(row[iElev]) : undefined,
      turnCost: iTurn >= 0 ? toInt(row[iTurn]) : undefined,
      nodeCost: iNode >= 0 ? toInt(row[iNode]) : undefined,
      initialCost: iInit >= 0 ? toInt(row[iInit]) : undefined,
      wayTags: iWay >= 0 && row[iWay] ? String(row[iWay]) : undefined,
      nodeTags: iNodeT >= 0 && row[iNodeT] ? String(row[iNodeT]) : undefined,
    })
  }
  return rows
}

/**
 * Match each parsed message row to the coordinate index whose microdegree
 * lon/lat equals the row's. Coordinates use degrees; rows use microdegrees.
 * Returns an array the same length as `rows`, with the matched index (or -1).
 */
function matchRowsToCoords(rows: ParsedMessageRow[], coords: number[][]): number[] {
  const indices: number[] = new Array(rows.length).fill(-1)
  let searchFrom = 0
  for (let r = 0; r < rows.length; r++) {
    const { lonMicro, latMicro } = rows[r]
    for (let i = searchFrom; i < coords.length; i++) {
      // Round coord degrees to microdegrees, matching BRouter's internal format.
      const lm = Math.round(coords[i][0] * 1_000_000)
      const am = Math.round(coords[i][1] * 1_000_000)
      if (lm === lonMicro && am === latMicro) {
        indices[r] = i
        searchFrom = i + 1
        break
      }
    }
  }
  return indices
}

function parseVoiceHints(voicehints: unknown): VoiceHintData[] {
  if (!Array.isArray(voicehints)) return []
  const hints: VoiceHintData[] = []
  for (const entry of voicehints) {
    if (!Array.isArray(entry)) continue
    const [idx, cmd, exit, dist, angle] = entry as unknown[]
    const nodeIndex = toInt(idx)
    const command = toInt(cmd)
    const distance = toInt(dist) ?? 0
    const ang = toNumber(angle) ?? 0
    if (nodeIndex === undefined || command === undefined) continue
    const exitNumber = toInt(exit)
    hints.push({
      nodeIndex,
      command,
      exitNumber: exitNumber && exitNumber > 0 ? exitNumber : undefined,
      distance,
      angle: ang,
    })
  }
  return hints
}

function computeDescents(points: RouteWaypoint[]): { plain: number; filtered: number } {
  let plain = 0
  let filtered = 0
  // "filtered" descend uses a simple 10m window smoothing — matches how
  // BRouter smooths ascend. We approximate by ignoring sub-1m drops.
  const threshold = 1
  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1].ele
    const cur = points[i].ele
    if (prev === undefined || cur === undefined) continue
    const delta = prev - cur
    if (delta > 0) {
      plain += delta
      if (delta >= threshold) filtered += delta
    }
  }
  return { plain: Math.round(plain), filtered: Math.round(filtered) }
}

function buildE2eRoute(coordinates: [number, number][], profile: string): RouteResult {
  const points: RouteWaypoint[] = []
  let totalDistance = 0

  for (let segmentIndex = 0; segmentIndex < coordinates.length - 1; segmentIndex += 1) {
    const [startLat, startLon] = coordinates[segmentIndex]
    const [endLat, endLon] = coordinates[segmentIndex + 1]

    for (let step = 0; step <= E2E_ROUTE_STEPS_PER_SEGMENT; step += 1) {
      if (segmentIndex > 0 && step === 0) continue

      const progress = step / E2E_ROUTE_STEPS_PER_SEGMENT
      const lat = startLat + (endLat - startLat) * progress
      const lon = startLon + (endLon - startLon) * progress

      if (points.length > 0) {
        const previous = points[points.length - 1]
        totalDistance += calculateHaversineDistance(previous.lat, previous.lon, lat, lon)
      }

      points.push({
        lat,
        lon,
        time_offset: totalDistance / E2E_ROUTE_SPEED_METERS_PER_SECOND,
      })
    }
  }

  const duration = totalDistance / E2E_ROUTE_SPEED_METERS_PER_SECOND

  return {
    points,
    coordinates: points.map((point) => [point.lat, point.lon]),
    distance: totalDistance,
    duration,
    creator: 'routecamp-e2e',
    profile,
    trackLength: Math.round(totalDistance),
    totalTime: Math.round(duration),
    filteredAscend: 0,
    plainAscend: 0,
    filteredDescend: 0,
    plainDescend: 0,
    totalEnergy: 0,
    cost: 0,
  }
}

/**
 * Builds a "route" for the fake `plane` profile: it simply connects the given
 * points with straight lines (point A → point B → …) without ever contacting
 * BRouter. Distance is the sum of the haversine distances between consecutive
 * points; no elevation or timing data is produced.
 */
function buildStraightLineRoute(coordinates: [number, number][], profile: string): RouteResult {
  let totalDistance = 0

  const points: RouteWaypoint[] = coordinates.map(([lat, lon], index) => {
    if (index > 0) {
      const previous = coordinates[index - 1]
      totalDistance += calculateHaversineDistance(previous[0], previous[1], lat, lon)
    }
    return { lat, lon }
  })

  return {
    points,
    coordinates: points.map((point) => [point.lat, point.lon]),
    distance: totalDistance,
    duration: 0,
    creator: 'routecamp-plane',
    profile,
    trackLength: Math.round(totalDistance),
    totalTime: 0,
    filteredAscend: 0,
    plainAscend: 0,
    filteredDescend: 0,
    plainDescend: 0,
    totalEnergy: 0,
    cost: 0,
  }
}

async function fetchBrouterResponse(
  url: string,
  signal: AbortSignal,
): Promise<BRouterResponse | null> {
  if (signal.aborted) return null

  if (isTauri()) {
    const { invoke } = await import('@tauri-apps/api/core')
    const response = await invoke<NativeBrouterRouteOutput>('native_brouter_route', {
      input: { url },
    })

    if (signal.aborted) return null

    if (response.status < 200 || response.status >= 300) {
      logService.error(LOG_CATEGORIES.MAP, `Native BRouter request failed (${response.status})`)
      return null
    }

    // The native command should always return a JSON string body. Guard against an
    // empty/undefined body (observed on iOS) so we fail routing gracefully instead of
    // throwing `JSON.parse(undefined)`, and log the actual shape to pinpoint the cause.
    if (typeof response.body !== 'string' || response.body.length === 0) {
      logService.error(LOG_CATEGORIES.MAP, 'Native BRouter returned an empty body', {
        status: response.status,
        bodyType: typeof response.body,
      })
      return null
    }

    try {
      return JSON.parse(response.body) as BRouterResponse
    } catch {
      logService.error(LOG_CATEGORIES.MAP, 'Native BRouter returned a non-JSON body', {
        status: response.status,
        bodyLength: response.body.length,
      })
      return null
    }
  }

  // In the browser during dev the BRouter host sends no CORS headers, so route the request through the
  // Vite dev proxy (see vite.config.ts `/brouter`). Native (Tauri) fetches server-side above and needs
  // no proxy; production web is same-origin/configured, so only rewrite for the dev fetch.
  const fetchUrl = import.meta.env.DEV ? url.replace(BROUTER_BASE, '/brouter/') : url
  const res = await fetch(fetchUrl, { signal })
  if (!res.ok) {
    logService.error(LOG_CATEGORIES.MAP, `BRouter request failed (${res.status})`)
    return null
  }

  return (await res.json()) as BRouterResponse
}

export const routingService = {
  /**
   * BRouter snaps coordinates to the road network during route calculation,
   * so explicit snapping is not needed. Returns the input as-is.
   */
  async getNearest(lat: number, lon: number): Promise<[number, number] | null> {
    return [lat, lon]
  },

  /**
   * Calculates a route between points using BRouter.
   * coordinates: Array of [lat, lon]
   */
  async getRoute(
    coordinates: [number, number][],
    profile: string = DEFAULT_ROUTING_PROFILE,
  ): Promise<RouteResult | null> {
    if (coordinates.length < 2) return null

    if (isStraightLineRoutingProfile(profile)) {
      return withPendingTracking(async () => buildStraightLineRoute(coordinates, profile))
    }

    if (isE2eMode()) {
      return withPendingTracking(async () => buildE2eRoute(coordinates, profile))
    }

    return withPendingTracking(async () => {
      const controller = new AbortController()
      pendingAbortControllers.add(controller)
      try {
        const brouterProfile = resolveBrouterProfileId(profile)
        // BRouter expects lon,lat pairs separated by pipes
        const lonlats = coordinates.map((c) => `${c[1]},${c[0]}`).join('|')
        const params = new URLSearchParams({
          lonlats,
          profile: brouterProfile,
          alternativeidx: '0',
          format: 'geojson',
          exportWaypoints: '1',
          timode: '2',
          voicehints: '1',
        })
        const url = `${BROUTER_BASE}?${params.toString()}`

        const json = await fetchBrouterResponse(url, controller.signal)
        if (!json) return null

        if (!json.features || json.features.length === 0) {
          logService.warn(LOG_CATEGORIES.MAP, 'BRouter returned no route features')
          return null
        }

        const feature = json.features[0]
        const props = feature.properties ?? {}
        const coords = feature.geometry.coordinates

        const messageRows = parseMessages(props['messages'])
        const rowCoordIndex = matchRowsToCoords(messageRows, coords)

        // Per-coordinate accumulated times (seconds since route start).
        const rawTimes = Array.isArray(props['times']) ? (props['times'] as unknown[]) : []

        // Build base waypoints with lat/lon/ele/time_offset.
        const points: RouteWaypoint[] = coords.map((c, i) => {
          const pt: RouteWaypoint = {
            lat: c[1],
            lon: c[0],
            ele: c.length > 2 && Number.isFinite(c[2]) ? c[2] : undefined,
          }
          const t = toNumber(rawTimes[i])
          if (t !== undefined) pt.time_offset = t
          return pt
        })

        // Forward-fill "range" fields (way_tags / cost_per_km / node_tags) onto
        // every waypoint from the previous boundary up to and including this
        // boundary. Boundary-only fields (distance, turn_cost, …) go only on
        // the boundary coordinate.
        let prevBoundary = -1
        for (let r = 0; r < messageRows.length; r++) {
          const boundaryIdx = rowCoordIndex[r]
          if (boundaryIdx < 0) continue
          const row = messageRows[r]
          for (let i = prevBoundary + 1; i <= boundaryIdx; i++) {
            if (row.wayTags) points[i].way_tags = row.wayTags
            if (row.costPerKm !== undefined) points[i].cost_per_km = row.costPerKm
          }
          const bp = points[boundaryIdx]
          if (row.nodeTags) bp.node_tags = row.nodeTags
          if (row.distance !== undefined) bp.distance = row.distance
          if (row.elevCost !== undefined) bp.elev_cost = row.elevCost
          if (row.turnCost !== undefined) bp.turn_cost = row.turnCost
          if (row.nodeCost !== undefined) bp.node_cost = row.nodeCost
          if (row.initialCost !== undefined) bp.initial_cost = row.initialCost
          // Fall back to message elevation if geometry didn't carry it.
          if (bp.ele === undefined && row.ele !== undefined) bp.ele = row.ele
          prevBoundary = boundaryIdx
        }

        const distance = toNumber(props['track-length']) ?? 0
        const duration = toNumber(props['total-time']) ?? 0
        const filteredAscend = toInt(props['filtered ascend'])
        const plainAscend = toInt(props['plain-ascend'])
        const totalEnergy = toInt(props['total-energy'])
        const cost = toInt(props['cost'])
        const creator = props['creator'] ? String(props['creator']) : undefined
        const profileName = props['name'] ? String(props['name']) : undefined
        const descents = computeDescents(points)
        const voiceHints = parseVoiceHints(props['voicehints'])

        const path: [number, number][] = points.map((p) => [p.lat, p.lon])

        return {
          points,
          coordinates: path,
          distance,
          duration,
          creator,
          profile: profileName ?? profile,
          trackLength: toInt(distance),
          totalTime: toInt(duration),
          filteredAscend,
          plainAscend,
          filteredDescend: descents.filtered,
          plainDescend: descents.plain,
          totalEnergy,
          cost,
          voiceHints: voiceHints.length ? voiceHints : undefined,
        }
      } catch (e) {
        if (e instanceof DOMException && e.name === 'AbortError') {
          return null
        }
        logService.error(LOG_CATEGORIES.MAP, 'BRouter route error', e)
        return null
      } finally {
        pendingAbortControllers.delete(controller)
      }
    })
  },
}
